import React, { useState, useEffect, useCallback } from 'react';
import KanbanBoard from '../../../components/KanbanBoard';
import {
  ClockCircleOutlined, CheckCircleOutlined, UserOutlined,
  PlusOutlined, CalendarOutlined, InfoCircleOutlined,
  MinusCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { Card, Typography, Tag, Button, message, Spin, Avatar, Space } from 'antd';
import api from '../../../api/axios';
import NewOrderModal from './NewOrderModal';
import OrderDetailModal from './OrderDetailModal';

const { Text, Title } = Typography;

const formatPrice = (p) => {
  if (!p && p !== 0) return '0';
  return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const STATUS_MAP = {
  pending:   { color: 'orange',  label: 'Kutilmoqda',     icon: <ClockCircleOutlined /> },
  confirmed: { color: 'blue',    label: 'Tasdiqlangan',   icon: <InfoCircleOutlined /> },
  in_progress:{ color: 'geekblue',label: 'Jarayonda',     icon: <ClockCircleOutlined /> },
  completed: { color: 'green',   label: 'Bajarildi',      icon: <CheckCircleOutlined /> },
  cancelled: { color: 'red',     label: 'Bekor qilingan', icon: <MinusCircleOutlined /> },
  rejected:  { color: 'volcano', label: 'Rad etilgan',    icon: <MinusCircleOutlined /> },
};

const KANBAN_COLUMNS = [
  { key: 'pending',      title: 'Kutilmoqda',   color: '#f59e0b', icon: <ClockCircleOutlined /> },
  { key: 'confirmed',    title: 'Tasdiqlangan', color: '#667eea', icon: <InfoCircleOutlined /> },
  { key: 'in_progress',  title: 'Jarayonda',    color: '#3b82f6', icon: <ClockCircleOutlined /> },
  { key: 'completed',    title: 'Bajarildi',    color: '#22c55e', icon: <CheckCircleOutlined /> },
];

const IMG = process.env.REACT_APP_URL;

// ═══════════════════════════════════════════════════
// ORDER CARD for Kanban
// ═══════════════════════════════════════════════════
const OrderCard = ({ order, onClick }) => {
  const st = STATUS_MAP[order.status] || STATUS_MAP.pending;
  const borderColor = st.color === 'orange' ? '#f59e0b' : st.color === 'blue' ? '#667eea' : st.color === 'green' ? '#22c55e' : '#3b82f6';
  return (
    <Card
      size="small"
      hoverable
      onClick={() => onClick(order)}
      style={{ borderRadius: 10, borderLeft: `3px solid ${borderColor}` }}
      styles={{ body: { padding: '10px 12px' } }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <Text strong style={{ fontSize: 13 }}>#{order.id}</Text>
        <Tag color={st.color} style={{ fontSize: 10, margin: 0 }}>{st.label}</Tag>
      </div>
      {order.client_name && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <Avatar size={20} icon={<UserOutlined />} src={order.client_avatar ? IMG + order.client_avatar : undefined} />
          <Text style={{ fontSize: 12 }}>{order.client_name}</Text>
        </div>
      )}
      {order.service_name && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{order.service_name}</Text>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 11, color: '#888' }}>
          <CalendarOutlined /> {order.day}/{order.month}/{order.year}
        </Text>
        {order.summa && (
          <Text strong style={{ fontSize: 12, color: '#22c55e' }}>{formatPrice(order.summa)} so'm</Text>
        )}
      </div>
      {order.start && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {String(order.start).slice(0, 5)} — {String(order.end).slice(0, 5)}
        </Text>
      )}
    </Card>
  );
};

// ═══════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════
const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [newOrderOpen, setNewOrderOpen] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    api.get('/orders/my')
      .then(res => setOrders(res.data?.data || []))
      .catch(() => message.error('Buyurtmalarni yuklashda xatolik'))
      .finally(() => setLoading(false));
  }, []);

  const fetchEmployees = useCallback(() => {
    api.get('/employees').then(res => setEmployees(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchOrders(); fetchEmployees(); }, [fetchOrders, fetchEmployees]);

  const openDetail = (order) => {
    setSelectedId(order.id);
    setDetailOpen(true);
  };

  // ─── Status change (Kanban drag) ───
  const handleMove = (item, toStatus) => {
    api.put(`/orders/${item.id}/status`, { status: toStatus })
      .then(() => {
        setOrders(prev => prev.map(o => o.id === item.id ? { ...o, status: toStatus } : o));
        message.success('Holat yangilandi');
      })
      .catch(() => message.error('Holatni o\'zgartib bo\'lmadi'));
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Buyurtmalar</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchOrders} style={{ borderRadius: 10 }}>
            Yangilash
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOrderOpen(true)} style={{ borderRadius: 10 }}>
            Yangi buyurtma
          </Button>
        </Space>
      </div>

      <NewOrderModal
        open={newOrderOpen}
        onClose={() => setNewOrderOpen(false)}
        onCreated={fetchOrders}
      />

      <OrderDetailModal
        open={detailOpen}
        orderId={selectedId}
        employees={employees}
        onClose={() => setDetailOpen(false)}
        onChanged={fetchOrders}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : (
        <KanbanBoard
          columns={KANBAN_COLUMNS}
          items={orders}
          columnKey="status"
          rowKey="id"
          columnMinWidth={320}
          renderCard={(item) => <OrderCard order={item} onClick={openDetail} />}
          onMove={handleMove}
          onRefresh={fetchOrders}
        />
      )}
    </div>
  );
};

export default Orders;
